import { differenceInDays } from 'date-fns';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { eraseCookie, getCookie } from '@/helpers/common';
import { COOKIE_NAME } from '@/helpers/cookie';
import { MODAL_QUERY_KEY, ModalQueryByName, Modals } from '@/helpers/modals';
import { STORAGE, useGetCommonStore, useLocalStorageStore } from '@/store';
import { useNavigation } from './navigation';

const NDB_LANDING_EXEMPTION_TTL_DAYS = 7;

// The registration-landing exemption only holds for 7 days after signup
const isOwnRegistrationLanding = (name: string, registeredAt?: string) => {
  if (!name) return false;

  const isExpired =
    !!registeredAt &&
    differenceInDays(new Date(), new Date(registeredAt)) >
      NDB_LANDING_EXEMPTION_TTL_DAYS;

  if (isExpired) {
    if (getCookie(COOKIE_NAME.NDB_LANDING) != null) {
      eraseCookie(COOKIE_NAME.NDB_LANDING);
    }
    if (useLocalStorageStore.getState().get(STORAGE.NDB_LANDING_CODE) != null) {
      useLocalStorageStore.getState().remove(STORAGE.NDB_LANDING_CODE);
    }
    return false;
  }

  const ndbLandingCookie = getCookie(COOKIE_NAME.NDB_LANDING);
  return ndbLandingCookie != null && ndbLandingCookie === name;
};

type TNavigate = ReturnType<typeof useNavigation>['navigate'];

interface IRedirectAuthenticatedVisitorProps {
  name: string;
  navigate: TNavigate;
  isRegistrationModalOpen: boolean;
  registeredAt: string | undefined;
}

const redirectAuthenticatedVisitor = ({
  name,
  isRegistrationModalOpen,
  navigate,
  registeredAt,
}: IRedirectAuthenticatedVisitorProps) => {
  if (isOwnRegistrationLanding(name, registeredAt) || isRegistrationModalOpen)
    return;

  navigate('/', false, true);
};

export const usePromoRedirect = (name: string) => {
  const { navigate } = useNavigation();
  const useCommonStore = useGetCommonStore();

  const { user, isAuthenticated } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      isAuthenticated: state.isAuthenticated,
    }))
  );
  const searchParams = useSearchParams();
  const isRegistrationModalOpen =
    searchParams.get(MODAL_QUERY_KEY) === ModalQueryByName[Modals.Registration];

  useEffect(() => {
    if (!isAuthenticated) return;

    redirectAuthenticatedVisitor({
      name,
      isRegistrationModalOpen,
      navigate,
      registeredAt: user?.createdAt,
    });
  }, [
    name,
    navigate,
    isAuthenticated,
    isRegistrationModalOpen,
    user?.createdAt,
  ]);
};
